import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { ocrAPI } from '../../services/api'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import {
  DocumentTextIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

// Silniki wymagające klucza API (cloud providers)
const CLOUD_ENGINES = ['openai', 'anthropic', 'xai', 'zai', 'google']

const PROVIDER_INFO = {
  openai:    { label: 'OpenAI',         placeholder: 'sk-...',       hint: 'platform.openai.com → API keys' },
  anthropic: { label: 'Anthropic',      placeholder: 'sk-ant-...',   hint: 'console.anthropic.com → API keys' },
  xai:       { label: 'xAI',            placeholder: 'xai-...',      hint: 'console.x.ai → API keys' },
  zai:       { label: 'z.ai',           placeholder: 'Bearer ...',   hint: 'z.ai → API keys' },
  google:    { label: 'Google Gemini',  placeholder: 'AIza...',      hint: 'aistudio.google.com → API keys' },
}

const SettingsPage = () => {
  const { t } = useTranslation('settings')
  const { user, updateProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [ocrEngines, setOcrEngines] = useState({})
  const [showApiKeys, setShowApiKeys] = useState({}) // { engineId: bool }
  const [ocrApiKeys, setOcrApiKeys] = useState({})   // { engineId: keyValue }

  // Ustawienia OCR
  const [ocrSettings, setOcrSettings] = useState({
    ocr_engine: 'easyocr',
    auto_categorization: true,
    extract_receipt_data: true,
    auto_confirm_receipts: true,
    confidence_threshold: 0.7
  })


  // Lista silników (lokalne + cloud)
  useEffect(() => {
    setOcrEngines({
      easyocr:         { name: 'EasyOCR',          available: true,  cloud: false },
      paddleocr:       { name: 'PaddleOCR',         available: true,  cloud: false },
      ai_llm:          { name: 'Ollama (lokalny)',   available: true,  cloud: false },
      transformers_llm:{ name: 'Transformers LLaVA', available: true, cloud: false },
      openai:          { name: 'OpenAI GPT-4o',     available: true,  cloud: true  },
      anthropic:       { name: 'Anthropic Claude',  available: true,  cloud: true  },
      xai:             { name: 'xAI Grok',          available: true,  cloud: true  },
      zai:             { name: 'z.ai',              available: true,  cloud: true  },
      google:          { name: 'Google Gemini',     available: true,  cloud: true  },
    })
  }, [])

  useEffect(() => {
    if (user && user.preferences) {
      setOcrSettings({
        ocr_engine: user.preferences.ocr_engine || 'easyocr',
        auto_categorization: user.preferences.auto_categorization ?? true,
        extract_receipt_data: user.preferences.extract_receipt_data ?? true,
        auto_confirm_receipts: user.preferences.auto_confirm_receipts ?? true,
        confidence_threshold: user.preferences.confidence_threshold || 0.7
      })
      setOcrApiKeys(user.preferences.ocr_api_keys || {})

}
  }, [user])

  const handleOcrSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      await updateProfile({
        preferences: {
          ...user.preferences,
          ...ocrSettings,
          ocr_api_keys: ocrApiKeys
        }
      })
      toast.success(t('ocr.success'))
    } catch (error) {
      toast.error(t('ocr.error'))
    } finally {
      setLoading(false)
    }
  }

  const toggleShowKey = (engineId) => {
    setShowApiKeys(prev => ({ ...prev, [engineId]: !prev[engineId] }))
  }

  const handleApiKeyChange = (engineId, value) => {
    setOcrApiKeys(prev => ({ ...prev, [engineId]: value }))
  }

  const handleClearKey = (engineId) => {
    setOcrApiKeys(prev => ({ ...prev, [engineId]: '' }))
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <LoadingSpinner size="lg" text={t('loading')} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('subtitle')}
          </p>
        </div>
      </div>

      {/* OCR Settings */}
      <Card>
          <Card.Header>
            <h3 className="text-lg font-medium text-[var(--color-text)]">{t('ocr.title')}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{t('ocr.subtitle')}</p>
          </Card.Header>
          <Card.Body>
            <form onSubmit={handleOcrSubmit} className="space-y-6">
              <div>
                <label htmlFor="ocr_engine" className="block text-sm font-medium text-[var(--color-text)]">
                  {t('ocr.engine')}
                </label>
                <select
                  id="ocr_engine"
                  value={ocrSettings.ocr_engine}
                  onChange={(e) => setOcrSettings({...ocrSettings, ocr_engine: e.target.value})}
                  className="mt-1 block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93]"
                >
                  {Object.entries(ocrEngines).map(([engineId, engineInfo]) => {
                    const getEngineLabel = (id, info) => {
                      if (id === 'easyocr') return `${info.name} (${t('ocr.recommended')})`
                      if (id === 'ai_llm') return `🤖 ${info.name} (${t('ocr.multimodalLLM')})`
                      if (id === 'transformers_llm') return `🚀 ${info.name} (${t('ocr.directLLM')})`
                      if (info.cloud) return `☁️ ${info.name}`
                      return info.name
                    }
                    return (
                      <option key={engineId} value={engineId} disabled={!engineInfo.available}>
                        {getEngineLabel(engineId, engineInfo)}
                        {!engineInfo.available && ` (${t('ocr.unavailable')})`}
                      </option>
                    )
                  })}
                </select>
                <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                  {t('ocr.engineDescription')}
                </p>
              </div>

              {/* Sekcja klucza API — widoczna tylko dla cloud providerów */}
              {CLOUD_ENGINES.includes(ocrSettings.ocr_engine) && (() => {
                const engineId = ocrSettings.ocr_engine
                const info = PROVIDER_INFO[engineId]
                const currentKey = ocrApiKeys[engineId] || ''
                const isKeySet = currentKey.length > 0
                return (
                  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-hover)] p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <KeyIcon className="h-4 w-4 text-[var(--color-text-muted)]" />
                      <h4 className="text-sm font-medium text-[var(--color-text)]">
                        Klucz API — {info.label}
                      </h4>
                      {isKeySet && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                          Ustawiony
                        </span>
                      )}
                    </div>

                    <div className="relative">
                      <input
                        type={showApiKeys[engineId] ? 'text' : 'password'}
                        value={currentKey}
                        onChange={(e) => handleApiKeyChange(engineId, e.target.value)}
                        placeholder={info.placeholder}
                        className="block w-full rounded-md border-[var(--color-border)] shadow-sm focus:border-[#1f9f93] focus:ring-[#1f9f93] pr-20 font-mono text-sm"
                        autoComplete="new-password"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
                        <button
                          type="button"
                          onClick={() => toggleShowKey(engineId)}
                          className="p-1 text-[var(--color-text-faint)] hover:text-[var(--color-text)]"
                          title={showApiKeys[engineId] ? 'Ukryj klucz' : 'Pokaż klucz'}
                        >
                          {showApiKeys[engineId]
                            ? <EyeSlashIcon className="h-4 w-4" />
                            : <EyeIcon className="h-4 w-4" />
                          }
                        </button>
                        {isKeySet && (
                          <button
                            type="button"
                            onClick={() => handleClearKey(engineId)}
                            className="text-xs text-[var(--color-error-text)] opacity-60 hover:opacity-100 px-1"
                            title="Usuń klucz"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-[var(--color-text-faint)]">
                      Klucz jest przechowywany lokalnie w Twoim profilu i używany wyłącznie do przetwarzania Twoich dokumentów.
                      Skąd pobrać: <span className="font-medium">{info.hint}</span>
                    </p>

                    {/* Konfiguracja pozostałych providerów */}
                    {CLOUD_ENGINES.filter(id => id !== engineId && ocrApiKeys[id]).length > 0 && (
                      <div className="pt-2 border-t border-[var(--color-border)]">
                        <p className="text-xs text-[var(--color-text-faint)] mb-2">Zapisane klucze dla innych providerów:</p>
                        <div className="flex flex-wrap gap-2">
                          {CLOUD_ENGINES.filter(id => id !== engineId && ocrApiKeys[id]).map(id => (
                            <span key={id} className="flex items-center gap-1 text-xs bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2 py-0.5">
                              <CheckCircleIcon className="h-3 w-3 text-emerald-500" />
                              {PROVIDER_INFO[id].label}
                              <button
                                type="button"
                                onClick={() => handleClearKey(id)}
                                className="text-[var(--color-text-faint)] hover:text-[var(--color-error-text)] ml-0.5"
                              >✕</button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}

              <div>
                <label htmlFor="confidence_threshold" className="block text-sm font-medium text-[var(--color-text)]">
                  {t('ocr.confidenceThreshold')} ({Math.round(ocrSettings.confidence_threshold * 100)}%)
                </label>
                <input
                  type="range"
                  id="confidence_threshold"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={ocrSettings.confidence_threshold}
                  onChange={(e) => setOcrSettings({...ocrSettings, confidence_threshold: parseFloat(e.target.value)})}
                  className="mt-1 block w-full"
                />
                <p className="mt-1 text-sm text-[var(--color-text-muted)]">
                  {t('ocr.confidenceDescription')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{t('ocr.autoCategorize')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{t('ocr.autoCategorizeDescription')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={ocrSettings.auto_categorization}
                    onChange={(e) => setOcrSettings({...ocrSettings, auto_categorization: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{t('ocr.extractReceipt')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{t('ocr.extractReceiptDescription')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={ocrSettings.extract_receipt_data}
                    onChange={(e) => setOcrSettings({...ocrSettings, extract_receipt_data: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-[var(--color-text)]">{t('ocr.autoConfirm')}</h4>
                    <p className="text-sm text-[var(--color-text-muted)]">{t('ocr.autoConfirmDescription')}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={ocrSettings.auto_confirm_receipts}
                    onChange={(e) => setOcrSettings({...ocrSettings, auto_confirm_receipts: e.target.checked})}
                    className="h-4 w-4 text-[#1f9f93] focus:ring-[#1f9f93] border-[var(--color-border)] rounded"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={loading}>
                  {loading ? t('common:saving') : t('common:save')}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>

    </div>
  )
}

export default SettingsPage
